import React, { useState } from "react"
import { Button, P2 } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { newArchAxiosInstance } from "utils/request"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { onQueryError } from "utils/utils"
import { IDptObj } from "../../interface/department"
import DepartmentKR from "./DepartmentKR"
import {
  KR,
  OBJ,
} from "pages/dashboard/performance/components/objectives/ObjCardComponents"
import { CreateEditKR } from "pages/dashboard/performance/components/objectives/CreateEditKR"
import { CreateEditDptObj } from "pages/dashboard/performance/components/objectives/CreateEditDptObj"
import { useAppNotificationStore, useAuthStore } from "stores"
import { useCycleDurationID } from "stores/cycle-duration"

interface props {
  data: IDptObj[] | undefined
  setOpenCreatObj?: (value: boolean) => void
  emptyStateText?: string
  activeObj: number | null
  setActiveObj: React.Dispatch<React.SetStateAction<number | null>>
  KRURL: string
  pageType: "admin" | "manager" | "employee"
}
const DepartmentFlow = ({
  data,
  emptyStateText,
  activeObj,
  setActiveObj,
  setOpenCreatObj,
  KRURL,
  pageType,
}: props) => {
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const { toast } = useAppNotificationStore()
  const cdId = useCycleDurationID({ type: pageType })

  const [modal, setModal] = useState<{
    type: "delete" | "view" | "add-kr" | "add-obj" | "edit" | ""
    data: IDptObj | null
  }>({
    type: "",
    data: null,
  })

  const { isLoading: isDeletingObj, mutate: deleteObj } = useMutation({
    mutationFn: async (id: string) => {
      const res = await newArchAxiosInstance.delete(
        `/api/v2/${
          pageType === "manager" ? "manager" : "admin"
        }/okr/departmentobjective/${modal?.data?.departmentObjectiveId}`
      )
      return res.data
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: [
            `/api/v2/admin/okr/departmentobjective/${modal?.data?.departmentId}`,
          ],
        })
        queryClient.invalidateQueries({
          queryKey: [`/api/v2/employee/okr/departmentobjectives/${cdId}`],
        })
        queryClient.invalidateQueries({
          queryKey: [
            `/api/v2/manager/okr/departmentobjectives?cycleDurationId=${cdId}`,
          ],
        })
        queryClient.invalidateQueries({
          queryKey: ["dpt-obj"],
        })
        setModal({
          type: "",
          data: null,
        })
        toast.success(data.message || "Operation successful")
      }
    },
    onError: onQueryError,
  })

  const canMakeChanges =
    pageType === "admin" || (pageType === "manager" && user?.isHOD)

  return (
    <div className="min-h-[429px]">
      {data && data?.length > 0 ? (
        <div className=" flex flex-col gap-4">
          {data?.map((item, index) => (
            <div
              key={index}
              className=" w-full bg-white p-5 shadow-sm  rounded-5px"
            >
              <div className="mb-3  flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <OBJ.CollapseExpandButton
                    expanded={item?.departmentObjectiveId === activeObj}
                    onClick={() =>
                      setActiveObj((prev) =>
                        prev === item?.departmentObjectiveId
                          ? null
                          : item?.departmentObjectiveId
                      )
                    }
                  />
                  <OBJ.ObjTypeImg objType="Department" />
                  <OBJ.ObjTypeTag
                    title={item?.departmentName}
                    objType="Department"
                  />
                </div>
                {canMakeChanges && (
                  <OBJ.ObjActionsBtn
                    onAddKR={() =>
                      setModal({
                        type: "add-kr",
                        data: item,
                      })
                    }
                    onEdit={() => {
                      setModal({
                        type: "edit",
                        data: item,
                      })
                    }}
                    onDelete={() => {
                      setModal({
                        type: "delete",
                        data: item,
                      })
                    }}
                    // onView={() => {
                    //   setModal({
                    //     type: "view",
                    //     data: item,
                    //   })
                    // }}
                  />
                )}
              </div>
              <div className=" mb-4 flex justify-between items-start ">
                <div>
                  <OBJ.ObjTitle title={item?.departmentObjectiveName} />
                  <OBJ.ObjCreatedDate date={item?.createdDate} />
                </div>

                <OBJ.ObjKRCountTag krCount={item?.keyResultCount} />
              </div>

              <OBJ.ObjProgress progress={item?.percentageProgress} />

              {item?.departmentObjectiveId === activeObj && (
                <KR.KRsWrapper>
                  {activeObj == item?.departmentObjectiveId && (
                    <DepartmentKR
                      departmentObjectiveId={item?.departmentObjectiveId}
                      KRURL={KRURL}
                      pageType={pageType}
                    />
                  )}

                  {canMakeChanges && (
                    <KR.KRAddButton
                      onClick={() =>
                        setModal({
                          type: "add-kr",
                          data: item,
                        })
                      }
                    />
                  )}
                </KR.KRsWrapper>
              )}
            </div>
          ))}

          {modal.type === "add-kr" && (
            <CreateEditKR
              onClose={() => {
                setModal({ type: "", data: null })
              }}
              open={true}
              role={pageType}
              deptId={data?.[0]?.departmentId}
              deptObjId={activeObj}
            />
          )}
        </div>
      ) : (
        <OBJ.ObjEmptyState
          showBtn={canMakeChanges}
          btnText={"Create Department Objective"}
          onCreate={() => {
            setOpenCreatObj?.(true)
          }}
          text={emptyStateText ?? "No department objectives yet."}
        />
      )}

      <CreateEditDptObj
        open={modal.type === "edit" || modal.type === "add-obj"}
        onClose={() => setModal({ type: "", data: null })}
        role={pageType === "admin" ? "admin" : "manager"}
        deptId={modal?.data?.departmentId}
        dptObjId={
          modal?.type === "edit"
            ? modal?.data?.departmentObjectiveId
            : undefined
        }
      />
      {modal.type === "add-kr" && (
        <CreateEditKR
          onClose={() => {
            setModal({ type: "", data: null })
          }}
          open={true}
          role={pageType === "admin" ? "admin" : "manager"}
          deptId={modal?.data?.departmentId}
          deptObjId={modal?.data?.departmentObjectiveId}
        />
      )}
      <CustomModal
        open={modal.type === "delete"}
        onCancel={() => {
          setModal({ type: "", data: null })
        }}
        title={`Delete ${modal?.data?.departmentObjectiveName}`}
        footer={
          <div className="flex justify-end gap-4">
            <Button
              color="neutral"
              onClick={() => {
                setModal({ type: "", data: null })
              }}
              // disabled={isDeletingGroup}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                deleteObj(
                  modal?.data?.departmentObjectiveId?.toString() as string
                )
              }}
              disabled={isDeletingObj}
              submitting={isDeletingObj}
            >
              Delete
            </Button>
          </div>
        }
      >
        <P2 className=" font-normal  font-avenir text-neutral500">
          This objective will be permanently deleted and cannot be recovered.
        </P2>
      </CustomModal>
    </div>
  )
}

export default DepartmentFlow
